import { G } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { BooleanInput, required, SelectInput, TextInput, useRecordContext } from 'react-admin';
import { useWatch } from 'react-hook-form';

import { languageChoices } from '@sh4res/bo-common/choices/language';
import { wideInputStyle } from '@sh4res/bo-common/components/B2bEmployee/common';
import { ShowSection } from '@sh4res/bo-common/components/ShowSection';
import { PhoneNumberInput } from '@sh4res/bo-common/fields';
import { inputDisabledProps } from '@sh4res/bo-common/utils';
import { useCountryCodeChoicesByName } from '@sh4res/bo-common/utils/countryChoices';
import { isBoExternal } from '@sh4res/bo-common/utils/env';
import { trimmedEmail } from '@sh4res/bo-common/validators';

type ContactStepProps = {
  disableEmailEdit?: boolean;
  disablePhoneNumberEdit?: boolean;
  disableAddressEdit?: boolean;
  isInAutoApprove?: boolean;
  testId?: string;
};

export const ContactStep = ({
  disableEmailEdit,
  disablePhoneNumberEdit,
  disableAddressEdit,
  isInAutoApprove,
  testId,
}: ContactStepProps) => {
  const countryCodeChoicesByName = useCountryCodeChoicesByName();
  const addressProps = {
    ...inputDisabledProps(!!disableAddressEdit),
    validate: disableAddressEdit ? undefined : [required()],
    isRequired: !disableAddressEdit,
  };
  const allowsManualOnboarding = useWatch({ name: 'allows_manual_onboarding' });
  const record = useRecordContext();

  const isCreating = G.isNullable(record?.id);
  const isActivatedByCompany = record?.is_activated_by_company ?? false;
  const isActivatedByEmployee = record?.activation_code_is_used ?? false;

  const isFullyActivated = isActivatedByCompany && isActivatedByEmployee;
  const hadEmail = G.isNotNullable(record?.email);
  const requireEmail = !isCreating && (hadEmail || isFullyActivated);

  const emailValidators = useMemo(() => {
    const validators = [];
    if (!disableEmailEdit) {
      if (requireEmail) {
        validators.push(required());
      }
      validators.push(trimmedEmail());
    }

    return validators;
  }, [disableEmailEdit, requireEmail]);

  const secondaryEmailValidators = [trimmedEmail()];

  return (
    <Box>
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.employees.labels.contact"
        noCard={true}>
        <TextInput
          sx={wideInputStyle}
          source="email"
          validate={emailValidators}
          isRequired={!disableEmailEdit && requireEmail}
          {...inputDisabledProps(!!disableEmailEdit)}
          data-testid={testId ? `${testId}_EmailInput` : undefined}
        />
        <PhoneNumberInput
          sx={wideInputStyle}
          source="phone_number"
          validate={[]}
          isRequired={false}
          mobileOnly
          {...inputDisabledProps(!!disablePhoneNumberEdit)}
          testId={testId ? `${testId}_PhoneNumberInput` : undefined}
        />
        <TextInput
          sx={wideInputStyle}
          source="secondary_email"
          validate={secondaryEmailValidators}
          isRequired={false}
          data-testid={testId ? `${testId}_SecondaryEmailInput` : undefined}
        />
        {G.isNullable(record?.id) &&
          !isBoExternal() &&
          allowsManualOnboarding &&
          !isInAutoApprove && (
            <BooleanInput
              data-testid={testId ? `${testId}_AutomaticOnboardingInput` : undefined}
              source="automatic_onboarding"
              label="resources.bo_common.b2b.employees.fields.automatic_onboarding"
              defaultValue={true}
            />
          )}
      </ShowSection>
      {!isInAutoApprove && (
        <ShowSection
          leftAligned
          label="resources.bo_common.b2b.employees.labels.address"
          noCard={true}>
          <TextInput
            sx={wideInputStyle}
            source="street"
            {...addressProps}
            data-testid={testId ? `${testId}_StreetInput` : undefined}
          />
          <TextInput
            sx={wideInputStyle}
            source="postalcode"
            {...addressProps}
            data-testid={testId ? `${testId}_PostalcodeInput` : undefined}
          />
          <TextInput
            sx={wideInputStyle}
            source="city"
            {...addressProps}
            data-testid={testId ? `${testId}_CityInput` : undefined}
          />
          <SelectInput
            source="country"
            validate={addressProps.validate}
            isRequired={addressProps.isRequired}
            choices={countryCodeChoicesByName}
            {...inputDisabledProps(!!disableAddressEdit)}
            data-testid={testId ? `${testId}_CountrySelect` : undefined}
          />
          <SelectInput
            source="language"
            choices={languageChoices}
            defaultValue={null}
            data-testid={testId ? `${testId}_LanguageSelect` : undefined}
          />
        </ShowSection>
      )}
    </Box>
  );
};
